import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getFormationProgression, saveFormationProgression } from "../../services/dataService";
const FormationContext = createContext(null);
export const useFormation = () => {
    const ctx = useContext(FormationContext);
    if (!ctx)
        throw new Error("useFormation doit être utilisé dans FormationProvider");
    return ctx;
};
export const FormationProvider = ({ children, portailType }) => {
    const { apiToken: accessToken } = useAuth();
    const [progressions, setProgressions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTutoriel, setActiveTutoriel] = useState(null);
    const [currentSteps, setCurrentSteps] = useState([]);
    const [navigationTarget, setNavigationTarget] = useState(null);
    const refreshProgression = useCallback(async () => {
        if (!accessToken)
            return;
        setLoading(true);
        try {
            const data = await getFormationProgression(accessToken);
            setProgressions(data.filter((p) => p.portailType === portailType));
        }
        catch { /* silent */ }
        finally {
            setLoading(false);
        }
    }, [accessToken, portailType]);
    useEffect(() => {
        refreshProgression();
    }, [refreshProgression]);
    const startTutoriel = useCallback((tutorielId, pType) => {
        // Charger les étapes déjà faites si existantes
        const existing = progressions.find((p) => p.tutorielId === tutorielId);
        setCurrentSteps(existing?.etapesCompletes || []);
        setActiveTutoriel(tutorielId);
        // Log du démarrage (fire and forget)
        if (accessToken) {
            saveFormationProgression(accessToken, {
                tutorielId,
                portailType: pType,
                etapesCompletes: existing?.etapesCompletes || [],
                statut: "En cours",
            }).catch(() => { });
        }
    }, [progressions, accessToken]);
    const stopTutoriel = useCallback((completed = false) => {
        if (activeTutoriel && accessToken) {
            saveFormationProgression(accessToken, {
                tutorielId: activeTutoriel,
                portailType,
                etapesCompletes: currentSteps,
                statut: completed ? "Termine" : "Interrompu",
            })
                .then(() => refreshProgression())
                .catch(() => { });
        }
        setActiveTutoriel(null);
        setCurrentSteps([]);
    }, [activeTutoriel, accessToken, portailType, currentSteps, refreshProgression]);
    const saveStep = useCallback((stepIndex) => {
        const stepKey = `step-${stepIndex}`;
        if (!currentSteps.includes(stepKey)) {
            setCurrentSteps((prev) => [...prev, stepKey]);
        }
    }, [currentSteps]);
    const isCompleted = useCallback((tutorielId) => {
        return progressions.some((p) => p.tutorielId === tutorielId && p.statut === "Termine");
    }, [progressions]);
    const isInProgress = useCallback((tutorielId) => {
        return progressions.some((p) => p.tutorielId === tutorielId && p.statut === "En cours");
    }, [progressions]);
    const clearNavigationTarget = useCallback(() => {
        setNavigationTarget(null);
    }, []);
    return (_jsx(FormationContext.Provider, { value: {
            progressions,
            loading,
            activeTutoriel,
            startTutoriel,
            stopTutoriel,
            saveStep,
            isCompleted,
            isInProgress,
            refreshProgression,
            navigationTarget,
            clearNavigationTarget,
        }, children: children }));
};
